/**
 * InvestorDocsGate — consolidated, server-validated investor/validator document gate.
 *
 * Replaces the deprecated footer-only PrivateDocsAccess component (June 12, 2026).
 * One passcode unlocks the catalog. The two valid passcodes (Founder/Admin →
 * full catalog, and the single Investor passcode → the curated investor docs)
 * are validated SERVER-SIDE only — no passcode literal is stored in this bundle.
 *
 * Flow: POST /api/investor-docs/unlock {passcode} → catalog. Each doc opens via
 * POST /api/investor-docs/open {passcode, filename} as a blob URL in a new tab —
 * the filename never appears in any public path.
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Unlock, FileText, ShoppingBag, FileDown } from 'lucide-react';
import HealthMartModal from '@/components/HealthMartModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Printable PDF exhibits for clinical validators (POST route keeps the passcode out of the URL).
const PDF_DOWNLOADS = {
  'CANT_MISS_DIFFERENTIAL_DIAGNOSIS_DEMO_2026-07.html': { ep: '/investor-docs/cant-miss-demo', name: 'P.AI_CantMiss_Differential_Diagnosis_Demo.pdf' },
  'CLINICAL_DIFFERENTIAL_CATALOG_2026-07.html': { ep: '/investor-docs/differential-catalog', name: 'P.AI_Differential_Diagnosis_Catalog_Engine83.pdf' },
};

export default function InvestorDocsGate() {
  const [passcode, setPasscode] = useState('');
  const [docs, setDocs] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHealthMart, setShowHealthMart] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [dossierBusy, setDossierBusy] = useState(false);
  const [schemaBusy, setSchemaBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState('');

  const grouped = useMemo(() => {
    if (!docs) return [];
    const order = [];
    const map = {};
    for (const d of docs) {
      if (!map[d.group]) { map[d.group] = []; order.push(d.group); }
      map[d.group].push(d);
    }
    return order.map(g => ({ group: g, items: map[g] }));
  }, [docs]);

  const tryUnlock = async () => {
    setError('');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/investor-docs/unlock`, {
        passcode,
        name: name.trim(),
        email: email.trim(),
        organization: organization.trim(),
      });
      if (r.data?.ok && Array.isArray(r.data.docs)) {
        setDocs(r.data.docs);
        setSessionId(r.data.session_id || '');
        setUnlocked(true);
      } else {
        setError('Could not verify passcode.');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) {
        setError(e?.response?.data?.detail || 'Please provide your name, organization, and a valid email.');
      } else if (status === 401) {
        setError('Invalid passcode.');
      } else {
        setError('Could not verify passcode.');
      }
    } finally {
      setBusy(false);
    }
  };

  const openDoc = async (doc) => {
    try {
      const r = await axios.post(
        `${API}/investor-docs/open`,
        { passcode, filename: doc.filename, session_id: sessionId },
        { responseType: 'text' }
      );
      const type = doc.format === 'html' ? 'text/html' : 'text/plain';
      const blob = new Blob([r.data], { type });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (w) setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError('Session expired — please re-enter passcode.');
        setUnlocked(false);
        setDocs(null);
        setPasscode('');
        setSessionId('');
      } else {
        setError('Could not open document.');
      }
    }
  };

  const downloadDossier = async () => {
    setError('');
    setDossierBusy(true);
    try {
      const r = await axios.post(`${API}/investor-docs/dossier`,
        { passcode, session_id: sessionId },
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'P.AI_Clinical_Engine_and_Architecture_Dossier_2026-07.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError('Session expired — please re-enter passcode.');
        setUnlocked(false);
        setDocs(null);
        setPasscode('');
        setSessionId('');
      } else {
        setError('Could not download the dossier PDF.');
      }
    } finally {
      setDossierBusy(false);
    }
  };

  const openArchitectureSchema = async () => {
    setError('');
    setSchemaBusy(true);
    const w = window.open('about:blank', '_blank');
    try {
      const r = await axios.post(`${API}/investor-docs/architecture-schema`,
        { passcode, session_id: sessionId },
        { responseType: 'text' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      if (w) { w.location.href = url; } else { window.open(url, '_blank'); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) { try { w.close(); } catch (_) { /* ignore */ } }
      if (e?.response?.status === 401) {
        setError('Session expired — please re-enter passcode.');
        setUnlocked(false);
        setDocs(null);
        setPasscode('');
        setSessionId('');
      } else {
        setError('Could not open the architecture schema.');
      }
    } finally {
      setSchemaBusy(false);
    }
  };

  const downloadDocPdf = async (doc) => {
    const cfg = PDF_DOWNLOADS[doc.filename];
    if (!cfg) return;
    setError('');
    setPdfBusy(doc.filename);
    try {
      const r = await axios.post(`${API}${cfg.ep}`,
        { passcode, session_id: sessionId },
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = cfg.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError('Session expired — please re-enter passcode.');
        setUnlocked(false);
        setDocs(null);
        setPasscode('');
        setSessionId('');
      } else {
        setError('Could not download the PDF.');
      }
    } finally {
      setPdfBusy('');
    }
  };

  if (!unlocked) {
    return (
      <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="investor-docs-gate-locked">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4 text-slate-500" />
          <p className="text-sm font-semibold text-slate-800">Investor / Validator Document Vault</p>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Company brief, architecture overview, founder traction, moats &amp; competitive contrast, and regulatory posture.
          Enter your <span className="font-semibold">investor passcode</span> for the curated set — or the founder/admin passcode for the full archive.
        </p>
        <div className="space-y-2">
          <Input
            type="text"
            placeholder="Full name (investors)"
            value={name}
            onChange={e => setName(e.target.value)}
            className="text-sm"
            data-testid="investor-docs-name-input"
            autoComplete="off"
          />
          <Input
            type="text"
            placeholder="Organization (investors)"
            value={organization}
            onChange={e => setOrganization(e.target.value)}
            className="text-sm"
            data-testid="investor-docs-org-input"
            autoComplete="off"
          />
          <Input
            type="email"
            placeholder="Email (investors)"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="text-sm"
            data-testid="investor-docs-email-input"
            autoComplete="off"
          />
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Passcode"
              value={passcode}
              onChange={e => setPasscode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && passcode && tryUnlock()}
              className="text-sm"
              data-testid="investor-docs-passcode-input"
              autoComplete="off"
            />
            <Button
              onClick={tryUnlock}
              disabled={!passcode || busy}
              className="bg-slate-800 hover:bg-slate-900 text-sm"
              data-testid="investor-docs-unlock-btn"
            >
              {busy ? '…' : 'Unlock'}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">
            Investors: please add your name, organization &amp; email. The founder/admin passcode doesn't require them.
          </p>
        </div>
        {error && <p className="mt-2 text-xs text-red-600" data-testid="investor-docs-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="border border-emerald-300 rounded-xl p-5 bg-emerald-50/40" data-testid="investor-docs-gate-unlocked">
      <div className="flex items-center gap-2 mb-4">
        <Unlock className="w-4 h-4 text-emerald-700" />
        <p className="text-sm font-semibold text-emerald-900">Investor / Validator Documents ({docs.length})</p>
        <button
          onClick={() => { setUnlocked(false); setDocs(null); setPasscode(''); setError(''); setSessionId(''); }}
          className="ml-auto text-xs text-slate-500 hover:text-slate-700 underline"
          data-testid="investor-docs-lock-btn"
        >
          Lock
        </button>
      </div>

      <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="investor-docs-disclaimer">
        <p className="text-xs font-bold text-red-600 tracking-wide uppercase mb-1">Pre-Pilot Notice — Read Before Reviewing</p>
        <p className="text-xs text-slate-700 leading-relaxed">
          These moat features represent the platform's architectural capabilities. Clinical and legal claims reflect intended production functionality; the current build is pre-pilot and uses synthetic data only, and has not yet been validated against real-world outcomes.
        </p>
      </div>

      <div className="mb-4 rounded-lg border border-teal-300 bg-white p-3.5" data-testid="dossier-download-card">
        <div className="flex items-start gap-2 mb-2">
          <FileDown className="w-4 h-4 text-teal-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-teal-900">Clinical Engine &amp; Architecture Dossier</p>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              One printable PDF for validators — the full 307-engine catalog with guideline citations, the architecture
              schema, the 8-layer safety stack, features, limitations &amp; governance.
            </p>
          </div>
        </div>
        <Button
          onClick={downloadDossier}
          disabled={dossierBusy}
          className="w-full rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm"
          data-testid="download-dossier-pdf-btn"
        >
          <FileDown className="w-4 h-4 mr-1.5" />
          {dossierBusy ? 'Preparing PDF…' : 'Download the Clinical Engine Dossier (PDF)'}
        </Button>
      </div>

      <div className="mb-4 rounded-lg border border-sky-300 bg-white p-3.5" data-testid="architecture-schema-card">
        <div className="flex items-start gap-2 mb-2">
          <FileText className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-sky-900">Full System Architecture Schema</p>
            <p className="text-[11px] text-slate-600 leading-relaxed">
              Standalone, printable — the 7-step deterministic pipeline, engine catalog, data model &amp;
              determinism contract. Live engine &amp; test figures injected at serve time (never stale).
            </p>
          </div>
        </div>
        <Button
          onClick={openArchitectureSchema}
          disabled={schemaBusy}
          className="w-full rounded-md bg-sky-700 hover:bg-sky-800 text-white text-sm"
          data-testid="open-architecture-schema-btn"
        >
          <FileText className="w-4 h-4 mr-1.5" />
          {schemaBusy ? 'Opening…' : 'Open the Full System Architecture Schema'}
        </Button>
      </div>

      <Button
        className="w-full mb-5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-sm"
        data-testid="gated-health-mart-btn"
      >
        <ShoppingBag className="w-4 h-4 mr-1.5" /> Explore P.AI Health Mart
      </Button>

      <div className="space-y-4">
        {grouped.map(({ group, items }) => (
          <div key={group}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-800 mb-1.5">{group}</p>
            <ul className="space-y-1.5">
              {items.map(d => (
                <li key={d.filename}>
                  <button
                    onClick={() => openDoc(d)}
                    className="inline-flex items-start gap-1.5 text-sm text-[#0369A1] hover:text-[#0284C7] hover:underline text-left"
                    data-testid={`investor-doc-link-${d.filename}`}
                  >
                    <FileText className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>{d.label}</span>
                  </button>
                  {PDF_DOWNLOADS[d.filename] && (
                    <button
                      onClick={() => downloadDocPdf(d)}
                      disabled={pdfBusy === d.filename}
                      className="mt-1 ml-5 inline-flex items-center gap-1.5 rounded-md border border-teal-600 bg-teal-50 px-2.5 py-1 text-[11px] font-bold text-teal-800 hover:bg-teal-100 disabled:opacity-60 transition-colors"
                      data-testid={`download-doc-pdf-${d.filename}`}
                    >
                      <FileDown className="w-3 h-3" />
                      {pdfBusy === d.filename ? 'Preparing PDF…' : 'Download PDF for validators'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {error && <p className="mt-3 text-xs text-red-600" data-testid="investor-docs-error">{error}</p>}

      <HealthMartModal open={showHealthMart} onOpenChange={setShowHealthMart} />
    </div>
  );
}
